/**
 * Applying the filters to the catalog — the "what's REALLY important" engine.
 *
 * Pure and synchronous over ~81 entries: cheap enough to recompute on every
 * keystroke, which is what makes a character chip feel instant. This is also
 * step 0 of the v0.2 solver ("hard filters"), kept here as its own function so
 * the solver can reuse it rather than re-implement it.
 *
 * The rules, in the order a reader would ask about them:
 *  - **Tier ceiling** — hide anything ranked above `tierMax`.
 *  - **Quality floor** — hide anything scoring below `qualityMin`, EXCEPT
 *    essentials while `essentialsIgnoreQuality` is on, and EXCEPT anything
 *    unrated. Quality is a separate axis from importance; the gate must never
 *    be able to cut an essential by accident — nor to cut an entry nobody has
 *    scored, which would be judging on absence. A film that isn't out yet has
 *    no rating and no votes, and hiding it behind a quality bar it was never
 *    measured against is the opposite of what the floor is for.
 *  - **Characters / threads** — OR within a facet: an entry survives if it
 *    links any selected character (and, separately, any selected thread).
 *    Selections are resolved against the catalog first, so a character id
 *    that no longer exists is ignored rather than emptying the road.
 *  - **Runtime cap** — on the *effective* cost, which is the compressed route
 *    when alternatives are enabled.
 *  - **Series** — drop `kind === "season"` when series are off.
 */

import type { Catalog, CharacterId, Entry, EntryId, ThreadId, Tier } from "@schema/catalog.ts";
import { DEFAULT_FILTERS, type FilterState } from "./types.ts";

/** What this entry costs you, honouring the compressed-route toggle. */
export function entryMinutes(entry: Entry, useAlternatives: boolean): number {
  return useAlternatives ? entry.solver.minMinutes : entry.runtime.minutes;
}

/** Catalog-dependent lookups hoisted out of the per-entry loop. */
export interface ResolvedFilters {
  filters: FilterState;
  tierRank: Map<Tier, number>;
  /** Selection ∩ catalog — see the note on stale ids above. */
  characters: Set<CharacterId>;
  threads: Set<ThreadId>;
}

export function resolveFilters(catalog: Catalog, filters: FilterState): ResolvedFilters {
  const tierRank = new Map(catalog.tiers.map((t) => [t.id, t.rank] as const));
  const knownCharacters = new Set(catalog.characters.map((c) => c.id));
  const knownThreads = new Set(catalog.threads.map((t) => t.id));
  return {
    filters,
    tierRank,
    characters: new Set(filters.characters.filter((id) => knownCharacters.has(id))),
    threads: new Set(filters.threads.filter((id) => knownThreads.has(id))),
  };
}

/** Which rule to skip — used to compute facet counts that ignore their own facet. */
type Facet = "characters" | "threads";

export function matchesFilters(entry: Entry, resolved: ResolvedFilters, skip?: Facet): boolean {
  const { filters } = resolved;

  const rank = resolved.tierRank.get(entry.curation.tier) ?? Number.POSITIVE_INFINITY;
  if (rank > filters.tierMax) return false;

  if (!filters.includeSeries && entry.kind === "season") return false;

  const exempt = filters.essentialsIgnoreQuality && entry.curation.tier === "essential";
  const score = entry.quality.score;
  if (!exempt && score !== null && score < filters.qualityMin) return false;

  if (
    filters.maxMinutes !== null &&
    entryMinutes(entry, filters.useAlternatives) > filters.maxMinutes
  ) {
    return false;
  }

  if (skip !== "characters" && resolved.characters.size > 0) {
    if (!entry.curation.characters.some((link) => resolved.characters.has(link.id))) return false;
  }

  if (skip !== "threads" && resolved.threads.size > 0) {
    if (!entry.curation.threads.some((link) => resolved.threads.has(link.id))) return false;
  }

  return true;
}

export interface FilterOutcome {
  filters: FilterState;
  resolved: ResolvedFilters;
  /** Surviving entries, in road order. */
  entries: Entry[];
  /** The same set, by id — what the road renders against. */
  visible: Set<EntryId>;
  /** Σ effective minutes over `entries`. */
  minutes: number;
  hidden: number;
  essentialsVisible: number;
  essentialsTotal: number;
  /**
   * Per-character result counts computed with every OTHER rule applied but
   * this facet ignored — standard faceted-search counts, so a chip's number
   * is what you'd see after clicking it, and chips never read "0" for
   * something that would in fact show you results.
   */
  characterCounts: Map<CharacterId, number>;
  threadCounts: Map<ThreadId, number>;
}

export function applyFilters(catalog: Catalog, filters: FilterState): FilterOutcome {
  const resolved = resolveFilters(catalog, filters);

  const entries: Entry[] = [];
  let minutes = 0;
  let essentialsVisible = 0;
  let essentialsTotal = 0;

  const characterCounts = new Map<CharacterId, number>();
  const threadCounts = new Map<ThreadId, number>();

  for (const entry of catalog.entries) {
    if (entry.curation.tier === "essential") essentialsTotal += 1;

    if (matchesFilters(entry, resolved)) {
      entries.push(entry);
      minutes += entryMinutes(entry, filters.useAlternatives);
      if (entry.curation.tier === "essential") essentialsVisible += 1;
    }

    if (matchesFilters(entry, resolved, "characters")) {
      for (const link of entry.curation.characters) {
        characterCounts.set(link.id, (characterCounts.get(link.id) ?? 0) + 1);
      }
    }
    if (matchesFilters(entry, resolved, "threads")) {
      for (const link of entry.curation.threads) {
        threadCounts.set(link.id, (threadCounts.get(link.id) ?? 0) + 1);
      }
    }
  }

  return {
    filters,
    resolved,
    entries,
    visible: new Set(entries.map((e) => e.id)),
    minutes,
    hidden: catalog.entries.length - entries.length,
    essentialsVisible,
    essentialsTotal,
    characterCounts,
    threadCounts,
  };
}

/**
 * How many controls are doing something — the number in the panel footer and
 * on the mobile "Filters" button.
 *
 * Counted per *control*, not per field: the quality floor and its
 * ignore-essentials modifier are one gate, and the modifier only counts while
 * the gate it modifies is actually on.
 */
export function activeFilterCount(filters: FilterState): number {
  let count = 0;
  if (filters.tierMax !== DEFAULT_FILTERS.tierMax) count += 1;
  if (
    filters.qualityMin !== DEFAULT_FILTERS.qualityMin ||
    (filters.qualityMin > 0 &&
      filters.essentialsIgnoreQuality !== DEFAULT_FILTERS.essentialsIgnoreQuality)
  ) {
    count += 1;
  }
  if (filters.characters.length > 0) count += 1;
  if (filters.threads.length > 0) count += 1;
  if (filters.maxMinutes !== null) count += 1;
  if (filters.includeSeries !== DEFAULT_FILTERS.includeSeries) count += 1;
  if (filters.useAlternatives !== DEFAULT_FILTERS.useAlternatives) count += 1;
  return count;
}
